import { Directive, HostListener, ElementRef } from '@angular/core';

@Directive({
  selector: '[appOnlyNumbers]'
})
export class OnlyNumbersDirective {

  constructor(private el: ElementRef) {}

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    const input = this.el.nativeElement as HTMLInputElement;
    const allowedKeys = [
      'Backspace', 'Tab', 'End', 'Home', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Delete'
    ];

    // Allow common shortcuts (select/copy/paste/cut/undo/redo).
    if ((event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x', 'z', 'y'].includes(event.key.toLowerCase())) {
      return;
    }

    if (allowedKeys.includes(event.key)) {
      return;
    }

    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
      return;
    }

    const currentValue = input.value;

    if (currentValue === '0' && event.key !== '0') {
      input.value = event.key;
      event.preventDefault();
      return;
    }

    // type="number" inputs do not reliably expose selectionStart/selectionEnd,
    // so avoid blocking here and let onInput clamp to [0, 100].
    if (input.type === 'number') {
      return;
    }

    const selectionStart = input.selectionStart ?? currentValue.length;
    const selectionEnd = input.selectionEnd ?? currentValue.length;
    const nextValue = currentValue.slice(0, selectionStart) + event.key + currentValue.slice(selectionEnd);

    if (/^0+$/.test(nextValue) && nextValue.length > 1) {
      event.preventDefault();
      return;
    }

    if (nextValue !== '' && parseInt(nextValue, 10) > 100) {
      event.preventDefault();
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent) {
    const input = this.el.nativeElement as HTMLInputElement;
    const pastedInput: string = (event.clipboardData?.getData('text') || '').trim();
    
    if (!/^\d+$/.test(pastedInput)) {
      event.preventDefault();
      return;
    }

    let pastedValue: number;

    if (input.type === 'number') {
      pastedValue = parseInt(pastedInput, 10);
    } else {
      const currentValue = input.value;
      const selectionStart = input.selectionStart ?? currentValue.length;
      const selectionEnd = input.selectionEnd ?? currentValue.length;
      const nextValue = currentValue.slice(0, selectionStart) + pastedInput + currentValue.slice(selectionEnd);
      pastedValue = parseInt(nextValue, 10);
    }

    if (isNaN(pastedValue) || pastedValue < 0 || pastedValue > 100) {
      event.preventDefault();
    }
  }

  @HostListener('input', ['$event'])
  onInput(event: Event) {
    const input = this.el.nativeElement as HTMLInputElement;
    const rawValue = input.value.trim();

    if (rawValue === '') {
      input.value = '';
      return;
    }

    if (!/^\d+$/.test(rawValue)) {
      input.value = '';
      return;
    }

    let value = parseInt(rawValue, 10);

    if (isNaN(value)) {
      input.value = '';
    } else if (value > 100) {
      input.value = '100';
    } else if (value < 0) {
      input.value = '0';
    }
  }
}
